<template>
    <div class="page-wrapper">
        <ScorecardContent
            v-if="isScorecardAvailable"
            :scorecardBody="scorecard.body"
            :accountInfo="scorecard.account"
            :createdAt="scorecard.createdAt"
            :overviewScore="scorecard.overviewScore"
            :showSection="sectionShown"
            mode="pdf"
        />
    </div>
</template>

<script lang="ts">
import { computed, ref, provide, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import * as Sentry from '@sentry/browser'

import ScorecardContent from '@/components/scorecard/ScorecardContent.vue'
import { handleIsScorecardAvailable } from '@/components/scorecard/utils'

import type { Scorecard } from '@opteo/types'

const baseUrl = 'https://opteo-netlify-cache.netlify.app/.netlify/functions'

export const getBlobCache = async (
    key: string,
    attemptsLeft = 3
): Promise<Scorecard.SingleScorecardFull> => {
    try {
        const response = await fetch(`${baseUrl}/cache-get`, {
            method: 'POST',
            body: JSON.stringify({ key }),
        })

        if (!response.ok) {
            throw new Error(`cache-get responded with status ${response.status}`)
        }

        return await response.json()
    } catch (error) {
        if (attemptsLeft <= 1) throw error

        await new Promise(resolve => setTimeout(resolve, 1000))
        return getBlobCache(key, attemptsLeft - 1)
    }
}

export default {
    name: 'ScorecardPdf',
    components: {
        ScorecardContent,
    },
    setup() {
        const router = useRouter()
        const scorecardTemporaryId = router.currentRoute.value.query.scorecardTemporaryId as string

        const scorecard = ref<Scorecard.SingleScorecardFull>()
        onMounted(async () => {
            try {
                scorecard.value = await getBlobCache(scorecardTemporaryId)
            } catch (error) {
                Sentry.captureException(error)
            }
        })

        const sectionShown = router.currentRoute.value.query
            .section as Scorecard.SectionTypeIncCover

        const isScorecardAvailable = computed(
            () => scorecard.value && handleIsScorecardAvailable(scorecard.value)
        )

        provide('isPdf', true)

        return {
            isScorecardAvailable,
            scorecard,
            sectionShown,
        }
    },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';
@import '@/assets/css/variables.scss';

.page-wrapper {
    padding-top: 6rem;
    padding-bottom: 6rem;
    max-width: 1148px;
    margin: 0 auto;
}
</style>
